import { observer, useSelector } from "@legendapp/state/react";
import type { RefObject, ReactNode } from "react";
import { chatMessages$ } from "@/state/chatMessagesLegend";
import {
  VirtualizedMessageList,
  type ChatMessageListRef,
} from "@/components/chat/VirtualizedMessageList";
import type { WebSearchCitation } from "@shared/schema";
import type { SourceContext } from "@/services/contextOrchestrator";

type RowMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  citations?: WebSearchCitation[];
  contextCitations?: Record<number, SourceContext>;
  images?: Array<{ url: string; detail?: string }>;
  model?: string;
  autoSelected?: boolean;
  status?: import("@/hooks/useChatSend").ChatMessageStatus;
  errorMessage?: string;
  errorKind?: import("@/lib/chatErrorCatalog").ChatErrorKind;
};

type Props = {
  renderMessage: (message: RowMessage) => ReactNode;
  typingIndicator?: ReactNode;
  researchIndicator?: ReactNode;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  containerRef?: RefObject<HTMLDivElement | null>;
  listRef?: RefObject<ChatMessageListRef | null>;
  onNearEndChange?: (nearEnd: boolean) => void;
};

/** Subscribes only this subtree to `chatMessages$` so the rest of ChatWorkspace can stay off the hot path. */
export const ChatMessagesLegendList = observer(function ChatMessagesLegendList({
  renderMessage,
  typingIndicator,
  researchIndicator,
  messagesEndRef,
  containerRef,
  listRef,
  onNearEndChange,
}: Props) {
  const messages = useSelector(chatMessages$) as RowMessage[];
  return (
    <VirtualizedMessageList
      messages={messages}
      renderMessage={renderMessage as (m: RowMessage) => ReactNode}
      typingIndicator={typingIndicator}
      researchIndicator={researchIndicator}
      messagesEndRef={messagesEndRef}
      containerRef={containerRef}
      listRef={listRef}
      onNearEndChange={onNearEndChange}
    />
  );
});
